import {
  BaseDropdown,
  BaseDropdownItem,
  TableButtonActionsCell,
  TableCell,
  TableImageCell,
  TableRow,
} from '../../../components';
import {
  ETypeFolder,
  documentStatus,
  folderTypeCategories,
} from '../../../constants';
import { useCanDocuments } from '../../../hooks';
import { type IFolderDTO } from '../../../services';
import { type IDocumentDTO } from '../../../services/documents';
import { Logger, dateFormatFromJSON } from '../../../utils';
import {
  ButtonAction,
  DropdownButtonContainer,
  FileIcon,
  TableCellCustom,
} from './styles';

interface IFolderBase
  extends Pick<IFolderDTO, 'accountId' | 'id' | 'typeFolder'> {}

interface IDocumentCardProps<
  TDocument extends IDocumentDTO,
  TFolder extends IFolderBase,
> {
  document: TDocument;
  folder: TFolder;
  userIsHighRole: boolean;
  disabledActions: boolean;
  userId: number;
  handleSelectDocument: (item: TDocument) => void;
  handleOnClipboardCopy?: (item: TDocument) => void;
  handleSelectDelete?: (item: TDocument) => void;
  handleSelectEdit?: (value: boolean) => void;
  handleOpenPreviewModal?: (value: boolean) => void;
  handleSetIsDropdownOpen?: (value: boolean) => void;
  handleDocumentStatusChange?: (
    id: number,
    status: string
  ) => Promise<void> | void;
}

export const DocumentCard = <
  TDocument extends IDocumentDTO,
  TFolder extends IFolderBase,
>({
  document,
  folder,
  userIsHighRole,
  userId,
  disabledActions,
  handleOnClipboardCopy,
  handleSelectDelete,
  handleSelectEdit,
  handleSelectDocument,
  handleOpenPreviewModal,
  handleSetIsDropdownOpen,

  handleDocumentStatusChange,
}: IDocumentCardProps<TDocument, TFolder>): JSX.Element => {
  const { allowEditAndDelete } = useCanDocuments({
    userId,
    userIsHighRole,
    folderData: {
      id: folder?.id,
      userCreateId: folder?.accountId,
      typeFolder: folder?.typeFolder ?? ETypeFolder.PRIVATE,
    },
  });

  return (
    <TableRow
      key={`${document.id} - ${
        document.name ?? document.originalName ?? 'unknown'
      }`}
      onClick={(e) => {
        e.stopPropagation();
        handleOpenPreviewModal?.(true);
        handleSelectDocument(document);
      }}
      data-cy={`documents-card-${document.id}-container`}
    >
      {folder?.typeFolder === ETypeFolder.RENEWAL && (
        <DocumentCardRenewal
          document={document}
          handleDocumentStatusChange={handleDocumentStatusChange}
          disabledActions={disabledActions}
        />
      )}
      {folder?.typeFolder !== ETypeFolder.RENEWAL && (
        <DocumentCardDefault
          document={document}
          userIsHighRole={userIsHighRole}
        />
      )}

      <TableCellCustom
        text={dateFormatFromJSON(document.createdAt, 'MM-dd-yyyy')}
        textAlign="end"
      />
      {!disabledActions && allowEditAndDelete ? (
        <DropdownButtonContainer
          data-cy="dropdown-container"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <BaseDropdown
            dataCy={'dropdown-menu-button'}
            onOpenChange={(isOpen) => {
              handleSetIsDropdownOpen?.(isOpen);
              Logger.debug('isOpen', isOpen);
            }}
          >
            {folderTypeCategories[folder.typeFolder] === 'public' && (
              <BaseDropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleOnClipboardCopy?.(document);
                }}
                data-cy={`documents-copyLink-${document.id}`}
              >
                Copy Link
              </BaseDropdownItem>
            )}
            <BaseDropdownItem
              data-cy={'edit-item'}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectDocument(document);
                handleSelectEdit?.(true);
              }}
            >
              Edit
            </BaseDropdownItem>

            <BaseDropdownItem
              data-cy={'delete-item'}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectDelete?.(document);
              }}
            >
              Delete
            </BaseDropdownItem>
            {folderTypeCategories[folder.typeFolder] === 'public' && (
              <BaseDropdownItem
                onClick={() =>
                  window.open(`${document.path}`, '_blank')?.focus()
                }
                data-cy={`documents-download-${document.id}`}
              >
                Download
              </BaseDropdownItem>
            )}
          </BaseDropdown>
        </DropdownButtonContainer>
      ) : (
        <div></div>
      )}
    </TableRow>
  );
};

interface IDocumentCardDefaultProps<TDocument extends IDocumentDTO> {
  document: TDocument;
  userIsHighRole: boolean;
}

const DocumentCardDefault = <TDocument extends IDocumentDTO>({
  document,
  userIsHighRole,
}: IDocumentCardDefaultProps<TDocument>): JSX.Element => {
  return (
    <>
      <TableImageCell
        text={
          document.originalName
            ? document.originalName
            : document.name
              ? `${document.name}.${document?.extension ?? ''}`
              : '-'
        }
        subtitle=""
        type="icon"
      >
        <FileIcon />
      </TableImageCell>
      <TableCellCustom
        dataCy={`documents-${document.id}-name`}
        text={document.name ? `${document.name}` : '-'}
        textAlign="start"
      />
      {userIsHighRole && (
        <TableCellCustom
          text={document.highlightedDate !== null ? 'Yes' : 'No'}
        />
      )}
    </>
  );
};

interface IDocumentCardRenewalProps<TDocument extends IDocumentDTO> {
  document: TDocument;
  handleDocumentStatusChange?: (
    id: number,
    status: string
  ) => Promise<void> | void;
  disabledActions: boolean;
}

const DocumentCardRenewal = <TDocument extends IDocumentDTO>({
  document,
  handleDocumentStatusChange,
  disabledActions,
}: IDocumentCardRenewalProps<TDocument>): JSX.Element => {
  return (
    <>
      <TableImageCell
        text={
          document.name
            ? `${document.name}.${document?.extension ?? ''}`
            : (document.originalName ?? 'unknown')
        }
        subtitle=""
        dataCy={`document-card-${document.id}`}
        type="icon"
      >
        <FileIcon />
      </TableImageCell>
      <TableButtonActionsCell dataCy={'button-actions-cell'}>
        <ButtonAction
          styleType="accept"
          textTransform="capitalize"
          fontWeight={500}
          disabled={disabledActions}
          $accept={document.status !== documentStatus.approved}
          onClick={(e) => {
            e.stopPropagation();
            if (document.status === documentStatus.approved) {
              return;
            }
            void handleDocumentStatusChange?.(
              document.id,
              documentStatus.approved
            );
          }}
        >
          {document.status === documentStatus.approved
            ? documentStatus.approved
            : 'Approve'}
        </ButtonAction>
        <ButtonAction
          styleType="decline"
          textTransform="capitalize"
          fontWeight={500}
          disabled={disabledActions}
          $accept={document.status !== documentStatus.declined}
          onClick={(e) => {
            e.stopPropagation();
            if (document.status === documentStatus.declined) {
              return;
            }
            void handleDocumentStatusChange?.(
              document.id,
              documentStatus.declined
            );
          }}
        >
          {document.status === documentStatus.declined
            ? documentStatus.declined
            : 'Decline'}
        </ButtonAction>
      </TableButtonActionsCell>

      <TableCell text={document?.documentType ?? ''} />
      <TableCell text={String(document?.createdBy?.fullName ?? '')} />
    </>
  );
};
